// Filters and positions tasks for a specific calendar day.
import { useMemo } from 'react';
import { isSameDay, isValid, parseISO } from 'date-fns';
import { calculateTaskPositions } from '@/features/tasks/utils/ui/taskPositions';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import {
  filterDayWorkUnits,
  isDayGridTask,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';

const YMD_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Local calendar date for grid filtering — YYYY-MM-DD strings use parseISO, not Date(string). */
const toDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date?.toDate === 'function') return date.toDate();
  if (typeof date === 'string' && YMD_DATE_RE.test(date)) return parseISO(date);
  const parsed = new Date(date);
  return isValid(parsed) ? parsed : null;
};

const useDayTasks = ({
  day,
  scheduledTasks,
  fifteenMinuteSlotHeight,
  startHour,
}) => {
  const { tasksMap } = useTasksContext();

  const tasksForDay = useMemo(() => {
    if (
      !day ||
      !scheduledTasks ||
      !fifteenMinuteSlotHeight ||
      fifteenMinuteSlotHeight === 0
    ) {
      return [];
    }

    const dayTasks = scheduledTasks.filter((task) => {
      if (!task.date || !task.time) {
        return false;
      }
      if (!isDayGridTask(task, tasksMap)) {
        return false;
      }
      const taskDay = toDate(task.date);
      return taskDay && isSameDay(taskDay, day.date);
    });
    const filteredTasks = filterDayWorkUnits(dayTasks, tasksMap);

    return calculateTaskPositions(
      filteredTasks,
      fifteenMinuteSlotHeight,
      startHour,
    );
  }, [day, scheduledTasks, fifteenMinuteSlotHeight, startHour, tasksMap]);

  const alldayTasks = useMemo(() => {
    if (!day || !scheduledTasks) {
      return [];
    }
    const dayTasks = scheduledTasks.filter((task) => {
      if (!task.date || task.time) return false;
      const taskDay = toDate(task.date);
      return (
        taskDay && isDayGridTask(task, tasksMap) && isSameDay(taskDay, day.date)
      );
    });
    return filterDayWorkUnits(dayTasks, tasksMap);
  }, [day, scheduledTasks, tasksMap]);

  return { tasksForDay, alldayTasks };
};

export default useDayTasks;
